import type { BaseApiResponse } from '@vueron/kiska';
import {
  type AxiosError,
  AxiosHeaders,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
  type Method,
} from 'axios';

type AxiosActionHistoryError = AxiosError<BaseApiResponse<any>> & {
  data?: BaseApiResponse<any>;
  status?: Nullish<number>;
};

type AxiosActionHistoryLog = {
  config: Pick<AxiosRequestConfig, 'baseURL' | 'data' | 'headers' | 'params'>;
  duration: number;
  error: Nullish<{
    message: string;
    status: Nullish<number>;
  }>;
  method: Nullish<Method>;
  response: { trID?: string } & Nullish<
    Pick<AxiosResponse, 'headers' | 'status'>
  >;
  timestamp: string;
  url: string;
};

// request 시작 시간을 저장하기 위한 확장 타입
type ExtendedAxiosRequestConfig = InternalAxiosRequestConfig & {
  _startTime?: number;
};

export const sanitizeAxiosHeaders = (headers: Nullish<AxiosHeaders>) => {
  if (!headers) return headers;

  const sanitizedHeaders = AxiosHeaders.from(headers);

  ['Authorization', 'X-Admin-OTP-Key'].forEach(headerName => {
    if (sanitizedHeaders.has(headerName)) {
      sanitizedHeaders.set(headerName, '[REDACTED]');
    }
  });

  return sanitizedHeaders.toJSON();
};

class AxiosActionHistory {
  static MAX_HISTORY = 10;
  static instance: AxiosActionHistory;

  actionHistory = [] as AxiosActionHistoryLog[];

  constructor() {
    if (AxiosActionHistory.instance) return AxiosActionHistory.instance;

    AxiosActionHistory.instance = this;
  }

  cleanup() {
    this.actionHistory = [];

    return this;
  }

  push(log: AxiosActionHistoryLog) {
    this.actionHistory.push(log);

    this.actionHistory.length > AxiosActionHistory.MAX_HISTORY &&
      this.actionHistory.shift();
  }

  // request interceptor에서 호출
  onRequest(config: ExtendedAxiosRequestConfig) {
    config._startTime = Date.now();

    return config;
  }

  // response interceptor에서 호출 (성공)
  onResponse(response: AxiosResponse<BaseApiResponse<any>>) {
    const config = response.config as ExtendedAxiosRequestConfig;
    const startTime = config._startTime ?? Date.now();
    const duration = Date.now() - startTime;

    const log: AxiosActionHistoryLog = {
      config: {
        baseURL: config.baseURL,
        data: config.data,
        headers: sanitizeAxiosHeaders(config.headers),
        params: config.params,
      },
      duration,
      error: null,
      method: config.method as Method,
      response: {
        trID: response.data?.trID ?? null,
        headers: response.headers,
        status: response.status,
      },
      timestamp: new Date().toISOString(),
      url: config.url ?? '',
    };

    this.push(log);

    return response;
  }

  // response interceptor에서 호출 (에러)
  onResponseError(error: AxiosActionHistoryError) {
    const config = error.config as ExtendedAxiosRequestConfig | undefined;
    const startTime = config?._startTime ?? Date.now();
    const duration = Date.now() - startTime;
    const responseData = error.response?.data ?? error.data;
    const status = error.response?.status ?? error.status ?? null;
    const trID = responseData?.trID ?? null;

    const log = {
      config: {
        baseURL: config?.baseURL,
        data: config?.data,
        headers: sanitizeAxiosHeaders(config?.headers),
        params: config?.params,
      },
      duration,
      error: {
        message: error.message,
        status,
      },
      method: config?.method as Method,
      response: error.response
        ? {
            trID,
            headers: error.response.headers,
            status,
          }
        : null,
      timestamp: new Date().toISOString(),
      url: config?.url ?? '',
    } as AxiosActionHistoryLog;

    this.push(log);

    return Promise.reject(error);
  }
}

export const axiosActionHistory = new AxiosActionHistory();
